// frontend/src/components/staff/SendConfirmModal.js
//
// MAILCHIMP-COMMS-001-B — Send-confirm modal.
// Spec: docs/design/MAILCHIMP-COMMS-001-A-design.md §5.
//
// Behavior:
//   - Surfaces a recipient summary (first 5 names + "+N more"), subject,
//     a compact preview of the rendered body, and a soft warning that
//     the send fires immediately.
//   - In-flight: spinner + "Sending..."; Back disabled; backdrop click +
//     close icon disabled.
//   - On Mailchimp/BE error: inline banner with mapped copy; Send re-enabled.
//     Form state is preserved upstream so the user can retry.

import React from "react";
import { FaInfoCircle, FaTimes, FaExclamationTriangle } from "react-icons/fa";
import "./SendConfirmModal.css";

const SendConfirmModal = ({
  recipients,
  subject,
  bodyHtml,
  sending,
  error,
  onCancel,
  onConfirm,
}) => {
  // Focus trap — extremely small, just trap Tab to within the modal.
  const modalRef = React.useRef(null);
  React.useEffect(() => {
    const root = modalRef.current;
    if (!root) return undefined;
    const focusable = root.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length) {
      const first = focusable[0];
      first.focus();
    }
    const onKey = (e) => {
      if (e.key === "Escape" && !sending) {
        e.stopPropagation();
        onCancel?.();
        return;
      }
      if (e.key === "Tab") {
        const list = root.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!list.length) return;
        const first = list[0];
        const last = list[list.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, sending]);

  const summary = buildRecipientSummary(recipients);

  return (
    <div
      className="broadcast-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="send-confirm-title"
      onClick={(e) => {
        if (sending) return; // backdrop click ignored mid-flight
        if (e.target === e.currentTarget) {
          onCancel?.();
        }
      }}
    >
      <div className="broadcast-modal" ref={modalRef}>
        <header className="broadcast-modal-header">
          <h2 id="send-confirm-title">Confirm send</h2>
          {!sending && (
            <button
              type="button"
              className="broadcast-modal-close"
              onClick={onCancel}
              aria-label="Close confirm dialog"
            >
              <FaTimes aria-hidden="true" />
            </button>
          )}
        </header>

        <div className="broadcast-modal-body">
          <p className="broadcast-modal-intro">About to send this message to:</p>
          <div className="broadcast-modal-recipient-count">
            {recipients.length} customer{recipients.length === 1 ? "" : "s"}
          </div>
          <div className="broadcast-modal-recipient-names">{summary}</div>

          <div className="broadcast-modal-subject">
            <strong>Subject:</strong> {subject || "(no subject)"}
          </div>

          <div className="broadcast-modal-preview-label">Message preview:</div>
          <div
            className="broadcast-modal-preview"
            // bodyHtml is the user's own input that the BE will re-sanitize
            // with DOMPurify before persistence + dispatch. The preview is
            // a closed admin/staff surface (RBAC-gated route). We surface
            // the raw HTML in the preview pane so the sender sees exactly
            // what their inputs render to — matching the visual spec's
            // "WYSIWYG live preview" intent. Defense-in-depth: this is a
            // staff/admin-only route, and the BE is the hard sanitization
            // gate (sanitization runs on the server before persistence
            // AND before dispatch — see commsController.js §"Sanitize body").
            dangerouslySetInnerHTML={{ __html: bodyHtml || "" }}
          />

          <div className="broadcast-modal-warning" role="status">
            <FaInfoCircle aria-hidden="true" />
            <span>
              This email will send immediately. Customers cannot opt out
              per-send; only the unsubscribe link applies.
            </span>
          </div>

          {error && (
            <div className="broadcast-modal-error" role="alert">
              <FaExclamationTriangle aria-hidden="true" />
              <span>{error.message}</span>
            </div>
          )}
        </div>

        <footer className="broadcast-modal-footer">
          <button
            type="button"
            className="broadcast-btn-outline"
            onClick={onCancel}
            disabled={sending}
          >
            Back
          </button>
          <button
            type="button"
            className="broadcast-btn-primary"
            onClick={onConfirm}
            disabled={sending}
            aria-disabled={sending}
          >
            {sending
              ? "Sending..."
              : `Send to ${recipients.length} customer${
                  recipients.length === 1 ? "" : "s"
                }`}
          </button>
        </footer>
      </div>
    </div>
  );
};

function buildRecipientSummary(recipients) {
  if (!Array.isArray(recipients) || recipients.length === 0) {
    return "No recipients selected.";
  }
  const names = recipients.map(
    (r) => r.displayName || r.email || "(unknown)"
  );
  if (names.length <= 5) {
    return names.join(", ");
  }
  const head = names.slice(0, 5).join(", ");
  const more = names.length - 5;
  return `${head}, +${more} more`;
}

export default SendConfirmModal;
